
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { getInitials as getInitialsFromLib, getDisplayName as getDisplayNameFromLib } from '@/components/utils/avatar-colors';

interface FormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  specialization: string;
  years_experience: string;
  avatar_url?: string;
}

interface BankAccountData {
  iban: string;
  account_holder: string;
  bank_name: string;
  swift_bic: string;
}

export const useUserProfile = () => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    specialization: '',
    years_experience: '',
    avatar_url: ''
  });

  const [bankData, setBankData] = useState<BankAccountData>({
    iban: '',
    account_holder: '',
    bank_name: '',
    swift_bic: ''
  });

  // Fetch user profile data from the unified users table
  const { data: userProfile, isLoading } = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      console.log('🔄 Fetching user profile for:', user.id, user.email);
      
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();
      
      if (error) {
        console.error('❌ Error fetching user profile:', error);
        throw error;
      }
      
      console.log('✅ User profile fetched:', data);
      return data;
    },
    enabled: !!user?.id
  });

  // Handle form data initialization when userProfile data is available
  useEffect(() => {
    console.log('Updating form data with profile:', userProfile);
    if (userProfile) {
      setFormData({
        first_name: userProfile.first_name || '',
        last_name: userProfile.last_name || '',
        email: userProfile.email || user?.email || '',
        phone: userProfile.phone || '',
        specialization: userProfile.specialization || '',
        years_experience: userProfile.years_experience || '',
        avatar_url: userProfile.avatar_url || ''
      });
    } else if (user?.email) {
      setFormData(prev => ({
        ...prev,
        email: user.email
      }));
    }
  }, [userProfile, user?.email]);

  const handleBankDataChange = (newData: BankAccountData) => {
    setBankData(newData);
  };

  // Update password
  const updatePassword = async (newPassword: string): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('🔄 Updating password...');

      const { error } = await supabase.auth.updateUser({ 
        password: newPassword 
      });

      if (error) {
        console.error('❌ Error updating password:', error);
        
        // Handle specific error cases
        if (error.message.includes('re-authenticate')) {
          return { 
            success: false, 
            error: 'Riautenticazione richiesta. Devi effettuare nuovamente l\'accesso per cambiare la password.' 
          };
        }
        
        return { 
          success: false, 
          error: error.message || 'Impossibile aggiornare la password' 
        };
      }
      
      console.log('✅ Password updated successfully');
      return { success: true };
      
    } catch (error) {
      console.error('❌ Unexpected error updating password:', error);
      return { 
        success: false, 
        error: 'Errore imprevisto durante l\'aggiornamento della password' 
      };
    }
  };

  const getInitials = () => {
    return getInitialsFromLib(formData.first_name, formData.last_name);
  };

  const getDisplayName = () => {
    return getDisplayNameFromLib(formData.first_name, formData.last_name);
  };

  return {
    user,
    userProfile,
    isLoading,
    isEditing,
    setIsEditing,
    formData,
    setFormData,
    bankData,
    handleBankDataChange,
    updatePassword,
    getInitials,
    getDisplayName
  };
};
